import React, { useState, useMemo } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/ui/button';
import DataTable from 'src/components/ui/data-table';
import { Plus, FileText, CheckCircle, XCircle, BookCopy } from 'lucide-react';

// Mock data for requirements
const mockRequirements = [
  {
    id: 1,
    registration_id: 'REQ-001',
    title: 'Senior Software Engineer',
    created_date: '2024-01-15',
    submitted_count: 15,
    success_count: 12,
    failed_count: 3,
    duplicate_count: 1,
  },
  {
    id: 2,
    registration_id: 'REQ-002',
    title: 'Product Manager',
    created_date: '2024-01-18',
    submitted_count: 8,
    success_count: 6,
    failed_count: 2,
    duplicate_count: 1,
  },
  {
    id: 3,
    registration_id: 'REQ-003',
    title: 'Data Scientist',
    created_date: '2024-01-20',
    submitted_count: 20,
    success_count: 18,
    failed_count: 2,
    duplicate_count: 1,
  },
  {
    id: 4,
    registration_id: 'REQ-004',
    title: 'DevOps Engineer',
    created_date: '2024-01-22',
    submitted_count: 5,
    success_count: 4,
    failed_count: 1,
    duplicate_count: 1,
  },
  {
    id: 5,
    registration_id: 'REQ-005',
    title: 'UX Designer',
    created_date: '2024-01-25',
    submitted_count: 12,
    success_count: 10,
    failed_count: 2,
    duplicate_count: 1,
  },
];

const SourceCandidate = () => {
  const navigate = useNavigate();
  const [requirements] = useState(mockRequirements);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const columns = useMemo(
    () => [
      {
        key: 'registration_id',
        header: 'Requirement ID',
        sortable: true,
        width: '15%',
        className: 'font-semibold',
        render: (_v, row) => row.registration_id,
      },
      {
        key: 'title',
        header: 'Job Title',
        sortable: true,
        width: '25%',
        tooltip: true,
        className: 'font-semibold',
        render: (_v, row) => row.title,
      },
      {
        key: 'created_date',
        header: 'Created Date',
        sortable: true,
        width: '15%',
        render: (_v, row) => formatDate(row.created_date),
      },
      {
        key: 'submitted_count',
        header: 'Submitted',
        sortable: true,
        width: '12%',
        align: 'center',
        className: 'font-semibold',
        render: (_v, row) => (
          <div className="flex items-center justify-center gap-1 text-blue-600">
            <FileText className="w-4 h-4" />
            <span>{row.submitted_count}</span>
          </div>
        ),
      },
      {
        key: 'success_count',
        header: 'Success',
        sortable: true,
        width: '12%',
        align: 'center',
        className: 'font-semibold',
        render: (_v, row) => (
          <div className="flex items-center justify-center gap-1 text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span>{row.success_count}</span>
          </div>
        ),
      },
      {
        key: 'failed_count',
        header: 'Failed',
        sortable: true,
        width: '12%',
        align: 'center',
        className: 'font-semibold',
        render: (_v, row) => (
          <div className="flex items-center justify-center gap-1 text-red-600">
            <XCircle className="w-4 h-4" />
            <span>{row.failed_count}</span>
          </div>
        ),
      },
      {
        key: 'duplicate_count',
        header: 'Duplicate',
        sortable: true,
        width: '12%',
        align: 'center',
        className: 'font-semibold',
        render: (_v, row) => (
          <div className="flex items-center justify-center gap-1 text-orange-600">
            <BookCopy className="w-4 h-4" />
            <span>{row.duplicate_count}</span>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Source Candidate"
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={() => navigate('/source-candidates/create')}
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
            Create Requirement
          </Button>
        }
      />
      <div className="flex-1 p-6 bg-muted">
        <div className="bg-card rounded-lg shadow-sm">
          <DataTable
            data={requirements}
            columns={columns}
            searchable
            pagination
            pageSize={10}
            onRowClick={(row) =>
              navigate(`/source-candidates/preview/${row.id}`)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SourceCandidate;
